/**
 * useHelpMenu — context-free state owner for the WS-DISC-3 Global
 * Help Menu disclosure.
 *
 * Pattern
 * -------
 * WAI-ARIA Disclosure pattern (NOT the modal-dialog pattern). The
 * trigger (HelpPill) owns `aria-expanded` mirroring the menu state;
 * the panel (HelpMenu) is `role="region"` so the page background
 * stays interactive. ESC and click-outside dismiss; focus is moved
 * into the panel on open but the surrounding chrome is NOT marked
 * inert (that is the explicit contract — users can navigate to a
 * help link and follow it without losing the panel's place).
 *
 * Why a hook
 * ----------
 * Two consumers need the open/close state (the pill that triggers
 * the open and the panel that owns ESC + click-outside dismissal),
 * and the parent Layout consumes neither — there is no useful place
 * to lift the state above. Keeping it local keeps Layout free of
 * help-menu plumbing while still allowing the two siblings to share
 * a single source of truth via a returned tuple.
 *
 * Return contract
 * ---------------
 * { open, toggle, close } is the state surface.
 * `anchorProps` is meant to spread onto the pill button — it carries
 * `aria-expanded` and `aria-controls` for the disclosure pattern.
 * `panelProps` is meant to spread onto the panel root — it carries
 * the matching `id` and a `ref` callback that the panel registers
 * for the click-outside detector below. The detector walks up the
 * DOM tree from the click target so any descendant click (including
 * portaled children) is correctly classified as "inside".
 *
 * Reduced motion
 * --------------
 * The hook does NOT own animation timing — the consumer's CSS reads
 * `prefers-reduced-motion: reduce` to skip the slide. Keeping motion
 * in CSS keeps the hook a thin state owner.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Stable ids so the pill's `aria-controls` resolves to the panel's
 *  `id` even when both render across portals. Suffix is `-v1` so a
 *  future redesign can land alongside without colliding. */
const PANEL_ID = 'chiefy-help-menu-panel-v1';

export type AnchorProps = {
    'aria-expanded': boolean;
    'aria-controls': string;
};

export type PanelProps = {
    id: string;
    ref: (el: HTMLElement | null) => void;
};

export type UseHelpMenu = {
    /** True when the panel is rendered. */
    open: boolean;
    /** Flip the panel open/closed. The pill calls this. */
    toggle: () => void;
    /** Force close. ESC, click-outside, and any explicit link-follow
     *  inside the panel all route through this. */
    close: () => void;
    /** Spread on the trigger (pill button). */
    anchorProps: AnchorProps;
    /** Spread on the panel root. */
    panelProps: PanelProps;
};

export function useHelpMenu(): UseHelpMenu {
    const [open, setOpen] = useState(false);
    // Hold the panel node so the document-level click handler can
    // distinguish "inside the panel" from "outside". A ref callback
    // (not useRef) is used so the panel's actual DOM node is
    // captured on mount; React's spread on the panel prop wires this
    // automatically.
    const panelRef = useRef<HTMLElement | null>(null);
    // Hold the trigger so ESC restores focus to it (disclosure
    // pattern: focus returns to the disclosing element on close).
    const triggerRef = useRef<HTMLElement | null>(null);

    const toggle = useCallback(() => {
        setOpen((prev) => {
            if (!prev) {
                // Snapshot the focused element so close() can restore
                // focus to it. The trigger is the typical case (the
                // pill button); we capture document.activeElement
                // generally so a keyboard-only "Tab to pill + Enter"
                // sequence still gets focus restoration.
                triggerRef.current = document.activeElement as HTMLElement | null;
            }
            return !prev;
        });
    }, []);

    const close = useCallback(() => {
        setOpen((prev) => {
            if (prev) {
                // Restore focus on close. Guarded — the captured node
                // may have been unmounted while the panel was open.
                const t = triggerRef.current;
                if (t && typeof t.focus === 'function') {
                    t.focus();
                }
            }
            return false;
        });
    }, []);

    // ESC dismiss is owned by the panel component (HelpMenu.tsx) via
    // the keyboard SSOT — it mounts a scoped `useKeyboardScope` only
    // while the panel is rendered, which keeps the stack discipline
    // clean (no stray empty scopes when the menu is closed).

    // Click-outside dismiss. The capture phase + microtask-deferred
    // mount avoid a race where the same click that opened the panel
    // is immediately classified as "outside" (the pill's click event
    // bubbles up to document before the open state is committed).
    useEffect(() => {
        if (!open) return;
        let installed = false;
        const onClick = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            const panel = panelRef.current;
            if (panel && panel.contains(target)) return;
            // Click is outside the panel — close. We don't restore
            // focus on outside-click because the user has already
            // chosen where to interact next (clicking a link, a
            // background button, etc.).
            setOpen(false);
        };
        // Defer installation so the click that opened the panel does
        // not also trigger this handler.
        const t = queueMicrotask(() => {
            document.addEventListener('mousedown', onClick);
            installed = true;
        });
        // queueMicrotask returns void — keep the variable shape
        // explicit for the cleanup branch.
        void t;
        return () => {
            if (installed) document.removeEventListener('mousedown', onClick);
        };
    }, [open]);

    const anchorProps: AnchorProps = {
        'aria-expanded': open,
        'aria-controls': PANEL_ID,
    };

    const panelProps: PanelProps = {
        id: PANEL_ID,
        ref: (el) => {
            panelRef.current = el;
        },
    };

    return { open, toggle, close, anchorProps, panelProps };
}
